/**
 * 成本预估弹窗组件 (v2.5)
 * 在用户提交配置后显示成本预估，要求用户确认后继续
 */
import React from 'react';
import { Modal, Descriptions, Button, Alert, Spin, Statistic, Row, Col } from 'antd';
import { DollarOutlined, ThunderboltOutlined, InfoCircleOutlined } from '@ant-design/icons';

// 汇率常量
const USD_TO_RMB = 7.1;

const EstimationModal = ({
  visible,
  loading,
  estimation,
  onConfirm,
  onCancel,
  hierarchyEnabled = false, // v3.1: 是否启用层级打标
}) => {
  if (!estimation && !loading) {
    return null;
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <DollarOutlined style={{ color: '#1890ff' }} />
          <span>成本预估</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          取消
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={onConfirm}
          loading={loading}
          disabled={loading || !estimation}
        >
          确认并开始打标
        </Button>,
      ]}
      width={600}
      maskClosable={false}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: '#666' }}>正在计算成本预估...</p>
        </div>
      )}

      {!loading && estimation && (
        <>
          <Alert
            message="请确认预估成本"
            description="以下是基于试打数据或采样计算的成本预估，实际成本可能略有差异。"
            type="info"
            icon={<InfoCircleOutlined />}
            showIcon
            style={{ marginBottom: 24 }}
          />

          {hierarchyEnabled && estimation.hierarchy_multiplier && (
            <Alert
              message="层级打标成本说明"
              description={`已自动计算层级打标成本（${estimation.hierarchy_multiplier}层 × 基础成本）。层级打标会对每一层进行独立的 API 调用。`}
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={12}>
              <Statistic
                title="预估总成本"
                value={estimation.estimated_total_cost * USD_TO_RMB}
                precision={4}
                prefix="¥"
                valueStyle={{ color: '#3f8600' }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="单行平均成本"
                value={estimation.avg_cost_per_row * USD_TO_RMB}
                precision={6}
                prefix="¥"
                valueStyle={{ color: '#1890ff' }}
              />
            </Col>
          </Row>

          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="总行数">
              {estimation.total_rows.toLocaleString()} 行
            </Descriptions.Item>
            <Descriptions.Item label="预估Token数">
              {estimation.estimated_tokens.toLocaleString()} tokens
            </Descriptions.Item>
            <Descriptions.Item label="采样来源">
              {estimation.sample_source === 'TRIAL_REUSE' ? (
                <span style={{ color: '#52c41a' }}>
                  <ThunderboltOutlined /> 复用试打数据（更准确）
                </span>
              ) : (
                <span style={{ color: '#faad14' }}>
                  静默采样 {estimation.sample_size} 行
                </span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="币种">
              CNY
            </Descriptions.Item>
          </Descriptions>

          {estimation.sample_source === 'SILENT_SAMPLING' && (
            <Alert
              message="提示"
              description="未找到试打记录，已通过静默采样估算成本。建议先进行试打以获得更准确的成本预估。"
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </>
      )}

      {!loading && !estimation && (
        <Alert
          message="预估失败"
          description="无法获取成本预估信息，请重试或联系管理员。"
          type="error"
          showIcon
        />
      )}
    </Modal>
  );
};

export default EstimationModal;
